import { ICellRendererParams } from '@amzn/gft-ag-grid-enterprise';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Checkbox, IconButton, TextField, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { FootnoteTableRow } from 'types/Footnotes.types';

interface LinkedCellRendererParams extends ICellRendererParams<FootnoteTableRow> {
  onChange: (rowId: string, linked: boolean) => void;
  isEditingDisabled?: boolean;
}

export const LinkedCellRenderer = (params: LinkedCellRendererParams) => {
  const { data, onChange, isEditingDisabled } = params;
  if (!data) return null;

  return (
    <Checkbox
      checked={data.linked}
      onChange={(e) => onChange(data.id, e.target.checked)}
      size='small'
      disabled={isEditingDisabled}
    />
  );
};

export const IdCellRenderer = (params: ICellRendererParams<FootnoteTableRow>) => {
  const { data } = params;
  if (!data) return null;

  return (
    <Typography variant='body2' component='span'>
      {data.footnoteNo}
    </Typography>
  );
};

interface DescriptionCellRendererParams extends ICellRendererParams<FootnoteTableRow> {
  onChange: (rowId: string, description: string) => void;
  isEditingDisabled?: boolean;
}

export const DescriptionCellRenderer = (params: DescriptionCellRendererParams) => {
  const { data, onChange, isEditingDisabled } = params;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data?.description || '');

  const handleStartEdit = useCallback(() => {
    setEditValue(data?.description || '');
    setIsEditing(true);
  }, [data?.description]);

  const handleSave = useCallback(() => {
    if (data) {
      onChange(data.id, editValue);
    }
    setIsEditing(false);
  }, [data, editValue, onChange]);

  const handleCancel = useCallback(() => {
    setEditValue(data?.description || '');
    setIsEditing(false);
  }, [data?.description]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSave();
      } else if (event.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  if (!data) return null;

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        size='small'
        fullWidth
        autoFocus
        variant='outlined'
      />
    );
  }

  return (
    <Box
      onClick={isEditingDisabled ? undefined : handleStartEdit}
      className={`description-cell ${isEditingDisabled ? 'description-cell--disabled' : 'description-cell--editable'}`}
    >
      <Typography variant='body2' component='span'>
        {data.description || 'Click to edit...'}
      </Typography>
    </Box>
  );
};

interface DeleteCellRendererParams extends ICellRendererParams<FootnoteTableRow> {
  onDelete: (rowId: string) => void;
  isEditingDisabled?: boolean;
}

export const DeleteCellRenderer = (params: DeleteCellRendererParams) => {
  const { data, onDelete, isEditingDisabled } = params;
  if (!data) return null;

  return (
    <IconButton
      onClick={() => onDelete(data.id)}
      size='small'
      aria-label='Delete footnote'
      disabled={isEditingDisabled}
    >
      <DeleteIcon fontSize='small' />
    </IconButton>
  );
};
